/**
 * Platform-admin session store (zustand) — the web console's auth state, kept entirely SEPARATE from
 * the tenant `useSession` (src/auth/session.ts). The access token lives in memory; the refresh token
 * is the httpOnly `beam_admin_refresh` cookie (web-only console — no SecureStore).
 *
 * Status: 'loading' (restore in progress) | 'authenticated' (MFA satisfied) | 'unauthenticated'.
 * During the login→MFA handshake the store stays 'unauthenticated' and exposes `stage` so the gate
 * routes to /verify-mfa or /enroll.
 */
import { create } from 'zustand';
import type { AdminMeResponse, AdminLoginStage } from '@beam/api-types';
import {
  adminLoginRequest,
  adminVerifyMfaRequest,
  adminEnrollRequest,
  adminConfirmEnrollRequest,
  adminRefreshRequest,
  adminLogoutRequest,
  adminMeRequest,
  setAdminTokenAccessor,
  setAdminCsrfAccessor,
  setAdminRefreshHandler,
} from '../api/adminClient';
import type { AdminMfaEnrollResponse } from '@beam/api-types';

type AdminStatus = 'loading' | 'authenticated' | 'unauthenticated';
type AdminStage = 'mfa_required' | 'enroll_required' | null;

interface AdminSessionState {
  token: string | null;
  csrf: string | null;
  admin: AdminMeResponse | { id: string; email: string; fullName: string } | null;
  status: AdminStatus;
  stage: AdminStage;

  restore: () => Promise<void>;
  login: (email: string, password: string) => Promise<AdminLoginStage>;
  verifyMfa: (code: string) => Promise<void>;
  enroll: () => Promise<AdminMfaEnrollResponse>;
  confirmEnroll: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAdminSession = create<AdminSessionState>((set, get) => ({
  token: null,
  csrf: null,
  admin: null,
  status: 'loading',
  stage: null,

  async restore() {
    try {
      const r = await adminRefreshRequest();
      if (!r.mfaSatisfied) {
        // A pre-MFA session survived but isn't usable for business routes — require a fresh login.
        set({ token: null, csrf: null, admin: null, status: 'unauthenticated', stage: null });
        return;
      }
      set({ token: r.token, csrf: r.csrfToken });
      const me = await adminMeRequest();
      set({ admin: me, status: 'authenticated', stage: null });
    } catch {
      set({ token: null, csrf: null, admin: null, status: 'unauthenticated', stage: null });
    }
  },

  async login(email, password) {
    const r = await adminLoginRequest(email, password);
    if (r.stage === 'authenticated') {
      // Trusted device — the server skipped TOTP and returned a full session token.
      set({ token: r.token, csrf: r.csrfToken, admin: r.admin, status: 'authenticated', stage: null });
    } else {
      set({ token: r.token, csrf: r.csrfToken, admin: r.admin, status: 'unauthenticated', stage: r.stage });
    }
    return r.stage;
  },

  async verifyMfa(code) {
    const r = await adminVerifyMfaRequest(code);
    set({ token: r.token, status: 'authenticated', stage: null });
  },

  async enroll() {
    return adminEnrollRequest();
  },

  async confirmEnroll(code) {
    const r = await adminConfirmEnrollRequest(code);
    set({ token: r.token, status: 'authenticated', stage: null });
  },

  async logout() {
    await adminLogoutRequest();
    set({ token: null, csrf: null, admin: null, status: 'unauthenticated', stage: null });
  },
}));

// Wire accessors into the admin client at module load (before any request).
setAdminTokenAccessor(() => useAdminSession.getState().token);
setAdminCsrfAccessor(() => useAdminSession.getState().csrf);
// On a 401, rotate the admin refresh token; only a full (MFA-satisfied) session yields a usable token.
setAdminRefreshHandler(async () => {
  try {
    const r = await adminRefreshRequest();
    if (!r.mfaSatisfied) {
      useAdminSession.setState({ token: null, csrf: null, admin: null, status: 'unauthenticated', stage: null });
      return null;
    }
    useAdminSession.setState({ token: r.token, csrf: r.csrfToken });
    return r.token;
  } catch {
    useAdminSession.setState({ token: null, csrf: null, admin: null, status: 'unauthenticated', stage: null });
    return null;
  }
});
